import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useCalendarStore } from "../../pages/Dashboard/Calendar/store/useCalendarStore";
import { CustomerList } from "./components/CustomerList";
import DateTimeSection from "./components/DateTimeSection";
import Form from "./components/Form";
import SelectedServiceList from "./components/SelectedServiceList";
import useAppointmentModal from "./useAppointmentModal";
import { getAppointmentEndTime, isAppointmentInPast } from "./utils";
import { useAppointmentModalStore } from "./useAppointmentModalStore_";
import ServiceSelect from "./components/ServiceSelect";
import {
  AccordionItem,
  addToast,
  Alert,
  Button,
  Divider,
  Link,
  Spinner,
  useDisclosure,
} from "@heroui/react";
import {
  AlertCircle,
  Check,
  CheckCircle,
  HeartPlus,
  TriangleAlert,
  Ticket,
} from "lucide-react";
import { LimeAccordion } from "../NextBase/LimeAccordion";
import { cn } from "@/utils";
import { RiGiftFill } from "react-icons/ri";
import { formatCurrency } from "../../../../shared/utils/utils";
import { i18n } from "@lingui/core";
import { ManageUserAppointmentAddons } from "./components/ManageUserAppointmentAddons";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import { LimeNumberInput } from "../NextBase/LimeNumberInput";
import { LimeSwitch } from "../NextBase/LimeSwitch";
import { PaymentDisputeCard } from "../PaymentDisputeCard";

const AppointmentModal = ({ appointmentId, openCustomerCard }) => {
  const [discountCode, setDiscountCode] = useState(null);

  const { setIsExpandedModal, serviceTags, calendarData } = useCalendarStore(
    (state) => ({
      setIsExpandedModal: state.setIsExpandedModal,
      serviceTags: state.serviceTags,
      calendarData: state.calendarData,
    }),
  );

  const {
    mutateAsync: putPrescheduledAppointmentData,
    isPending: isPuttingPrescheduledAppointmentData,
  } = api.appointments.usePutPrescheduledAppointmentData();

  const requiredTags = useAppointmentModalStore((state) => state.requiredTags);

  const {
    appointmentData,
    isFetchingData,
    handleDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
    services,
    selectedServices,
    setSelectedServices,
    confirmedServiceSelection,
    handleConfirmServiceSelection,
    selectedUserAppointment,
    handleUserAppointmentSelect,
    handleSubmitAppointmentForm,
    fetchAppointmentData,
    modifyAppointmentData,
  } = useAppointmentModal(appointmentId);

  const { isFeatureEnabled } = useClientFeature();

  const isNewAppointment = appointmentId === -1;
  const dateObject = dayjs(appointmentData?.startTime);

  const isInPast = useMemo(
    () =>
      isAppointmentInPast(
        appointmentData ? dateObject.format("YYYY-MM-DD HH:mm") : undefined,
      ),
    [appointmentData?.startTime],
  );

  const selectedCustomerAppointment = useMemo(
    () =>
      appointmentData?.customers?.find(
        (customer) => customer.userAppointmentId === selectedUserAppointment,
      ),
    [appointmentData, selectedUserAppointment],
  );

  const addons = useMemo(() => {
    const customerId = selectedCustomerAppointment?.customerId;
    if (!customerId) {
      return [];
    }

    return (
      appointmentData?.customers?.flatMap((customer) =>
        customer.customerId === customerId
          ? customer.addons.map((addon) => ({
              ...addon,
              customerId: customer.customerId,
              serviceId: customer.serviceId,
            }))
          : [],
      ) ?? []
    );
  }, [appointmentData, selectedCustomerAppointment]);

  const isPreScheduled = appointmentData?.isPreScheduled;

  const endTime = useMemo(
    () =>
      getAppointmentEndTime(
        appointmentData?.startTime,
        selectedServices,
        addons,
        isPreScheduled,
      ),
    [appointmentData?.startTime, selectedServices, addons, isPreScheduled],
  );

  const addonsDuration = useMemo(() => {
    if (isPreScheduled) {
      return 0;
    }

    return (
      addons?.reduce(
        (acc, addon) => acc + (addon.durationMinsAdjustment || 0),
        0,
      ) || 0
    );
  }, [addons, isPreScheduled]);
  useEffect(() => {
    setIsExpandedModal(isPreScheduled);
  }, [appointmentData?.isPreScheduled]);

  useEffect(() => {
    setDiscountCode(null);
  }, [appointmentId]);

  const content = useMemo(() => {
    if (isFetchingData) return <Spinner />;
    if (!appointmentData) return <div>Invalid appointment</div>;

    if (isNewAppointment && !confirmedServiceSelection) {
      return (
        <ServiceSelect
          services={services}
          serviceTags={serviceTags}
          selectedServices={selectedServices}
          setSelectedServices={setSelectedServices}
          setConfirmedServiceSelection={handleConfirmServiceSelection}
        />
      );
    }

    const selectedCustomerId = appointmentData.customers.find(
      (customer) => customer.userAppointmentId === selectedUserAppointment,
    )?.customerId;

    return (
      <Form
        customerData={appointmentData.customers.find(
          (customer) => customer.userAppointmentId === selectedUserAppointment,
        )}
        notificationSchedule={appointmentData.customers.flatMap((c) =>
          c.customerId === selectedCustomerId ? c.notificationSchedule : [],
        )}
        notificationLog={appointmentData.customers.flatMap((c) =>
          c.customerId === selectedCustomerId ? c.notificationLog : [],
        )}
        disabledNotificationSettingIds={Array.from(
          new Set(
            appointmentData.customers.flatMap((c) =>
              c.customerId === selectedCustomerId
                ? (c.disabledNotificationSettingIds ?? [])
                : [],
            ),
          ),
        )}
        handleSubmitAppointmentForm={handleSubmitAppointmentForm}
        isNewAppointment={isNewAppointment}
        appointmentLocationId={appointmentData.locationId}
        selectedServices={selectedServices}
        dayObject={dateObject}
        fetchAppointmentData={fetchAppointmentData}
        isExistingUserAppointmentSelected={selectedUserAppointment != null}
        discountCode={discountCode}
      />
    );
  }, [
    isFetchingData,
    appointmentData,
    isNewAppointment,
    confirmedServiceSelection,
    services,
    serviceTags,
    selectedServices,
    setSelectedServices,
    handleConfirmServiceSelection,
    selectedUserAppointment,
    handleSubmitAppointmentForm,
    dateObject,
    discountCode,
  ]);

  const successes = [];
  const warnings = [];
  const errors = [];
  if (isInPast) {
    if (isNewAppointment) {
      errors.push({
        message: t`Termin bo ustvarjen v preteklosti!`,
        variant: "filled",
      });
    } else {
      errors.push({
        message: t`Termin je v preteklosti!`,
        variant: "filled",
      });
    }
  }

  // REQUIRED SERVICES
  if (requiredTags.length) {
    errors.push({
      message: t`Potrebujete še storitev iz ${requiredTags.map((t) => serviceTags.find((st) => st.tagId === t)?.name).join(", ")}`,
    });
  }

  const { days } = calendarData;

  if (appointmentData?.startTime && days != null) {
    const date = dayjs(appointmentData.startTime, "YYYY-MM-DD HH:mm").format(
      "YYYY-MM-DD",
    );

    const selectedDay = days.find((day) => day.date === date);

    if (selectedDay?.isHoliday) {
      errors.push({
        message: t`Termin je na praznik!`,
        variant: "filled",
      });
    }
  }

  if (!isNewAppointment) {
    if (appointmentData) {
      if (
        appointmentData.isPreScheduled &&
        !appointmentData.showOnFormPreScheduled
      ) {
        warnings.push({
          message: t`Ta skupinski termin ni prikazan na spletni formi.`,
        });
      }

      if (appointmentData.customers?.some((c) => c.bookedFromMarketplace)) {
        successes.push({
          message: t`Termin je bil naročen preko platforme.`,
        });
      }
    }
  }

  const alerts = {
    successes,
    warnings,
    errors,
  };

  const selectedCustomerId = selectedCustomerAppointment?.customerId;

  const {
    isOpen: isManageUserAppointmentAddonsOpen,
    onOpen: onOpenManageUserAppointmentAddons,
    onClose: onCloseManageUserAppointmentAddons,
  } = useDisclosure();

  return (
    <div
      className={cn("overflow-y-auto", {
        "min-h-0 flex-1 [container-type:size]": isPreScheduled,
      })}
    >
      <div className="my-1 flex w-full flex-wrap gap-1 px-2">
        {alerts && (
          <>
            {alerts.errors.map((alert, index) => (
              <Alert
                key={index}
                title={alert.message}
                className="w-full px-0 py-0 text-center"
                classNames={{
                  base: "flex items-center justify-center",
                  title: "text-xs",
                  mainWrapper: "min-h-7",
                }}
                color="danger"
                hideIcon
                endContent={<AlertCircle size={16} className="mr-3" />}
              />
            ))}
            {alerts.warnings.map((alert, index) => (
              <Alert
                key={index}
                title={alert.message}
                className="w-full px-0 py-0 text-center"
                classNames={{
                  base: "flex items-center justify-center",
                  title: "text-xs",
                  mainWrapper: "min-h-7",
                }}
                color="warning"
                hideIcon
                endContent={<TriangleAlert size={16} className="mr-3" />}
              />
            ))}
            {alerts.successes.map((alert, index) => (
              <Alert
                key={index}
                title={alert.message}
                className="w-full px-0 py-0 text-center"
                classNames={{
                  base: "flex items-center justify-center",
                  title: "text-xs",
                  mainWrapper: "min-h-7",
                }}
                color="success"
                hideIcon
                endContent={<CheckCircle size={16} className="mr-3" />}
              />
            ))}
          </>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {selectedCustomerAppointment?.discountCodeUsages?.length > 0 && (
          <DiscountCodeUsageDetails
            discountCodeUsages={selectedCustomerAppointment.discountCodeUsages}
          />
        )}

        {selectedCustomerAppointment && (
          <LeftToPayWarning
            userAppointments={
              isPreScheduled
                ? [selectedCustomerAppointment]
                : appointmentData.customers
            }
          />
        )}

        {((isPreScheduled && selectedUserAppointment) || !isPreScheduled) && (
          <PaymentDetails
            customer={
              isPreScheduled
                ? selectedCustomerAppointment
                : appointmentData?.customers[0]
            }
          />
        )}

        {selectedCustomerAppointment &&
          selectedCustomerAppointment.giftCardTransaction && (
            <GiftCardTransactionDetails
              giftCardTransaction={
                selectedCustomerAppointment.giftCardTransaction
              }
            />
          )}

        {addons && addons.length > 0 && <AddonDetails addons={addons} />}
      </div>

      {selectedCustomerAppointment && isFeatureEnabled("serviceAddons") ? (
        <Button
          size="sm"
          color="primary"
          variant="flat"
          className="mt-2 ml-2"
          startContent={<HeartPlus size={16} />}
          onPress={onOpenManageUserAppointmentAddons}
        >
          <Trans>Uredi dodatke</Trans>
        </Button>
      ) : null}

      {isFetchingData || appointmentData == null ? (
        <Spinner className="mt-4" />
      ) : (
        <div className="relative flex flex-col gap-8 sm:flex-row">
          <div
            className={cn("flex max-w-full flex-1 flex-col", {
              "sm:sticky sm:top-0 sm:max-h-[100cqh] sm:min-w-min sm:self-start sm:overflow-y-auto":
                isPreScheduled,
            })}
          >
            {(confirmedServiceSelection || !isNewAppointment) && (
              <SelectedServiceList
                services={services}
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
                isPreScheduled={appointmentData.isPreScheduled}
                isNewAppointment={isNewAppointment}
                addons={addons}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
              />
            )}
            <DateTimeSection
              date={appointmentData.startTime}
              startTime={dateObject?.format("HH:mm") ?? "00:00"}
              endTime={endTime ?? "00:00"}
              duration={
                selectedServices.length === 0
                  ? 0
                  : selectedServices
                      .filter((s) => !s.removed)
                      .reduce((acc, service, i) => {
                        const serviceDuration =
                          (service.startDuration || 0) +
                          (service.endDuration || 0) +
                          (service.timeOffDuration || 0);
                        if (i === 0) {
                          return acc + serviceDuration;
                        }

                        const currentUsers = service.users || [];
                        const currentResources = service.resources || [];

                        const prevUsers = selectedServices[i - 1]?.users || [];
                        const prevResources =
                          selectedServices[i - 1]?.resources || [];

                        const areUsersOverlapping = currentUsers.some((user) =>
                          prevUsers.includes(user),
                        );

                        const areResourcesOverlapping = currentResources.some(
                          (resource) => prevResources.includes(resource),
                        );

                        if (areUsersOverlapping || areResourcesOverlapping) {
                          return (
                            acc +
                            serviceDuration +
                            (service.blockBeforeMins || 0) +
                            (selectedServices[i - 1]?.blockAfterMins || 0)
                          );
                        }

                        return acc + serviceDuration;
                      }, 0) + addonsDuration
              }
              handleDateChange={handleDateChange}
              handleStartTimeChange={handleStartTimeChange}
              handleEndTimeChange={handleEndTimeChange}
            />
            {isPreScheduled ? (
              <>
                <div className="flex flex-col gap-2">
                  <LimeNumberInput
                    data-identifier={"appointment-number-of-people-input"}
                    label={t`Število udeležencev`}
                    minValue={1}
                    maxValue={99999}
                    step={1}
                    size="sm"
                    value={appointmentData.numberOfPeople}
                    onValueChange={(value) => {
                      modifyAppointmentData({
                        numberOfPeople: value ?? 1,
                      });
                    }}
                  />

                  <LimeSwitch
                    title={t`Dovoli podvojene stranke na terminu`}
                    checked={
                      appointmentData.allowDuplicateCustomerOnAppointment
                    }
                    onValueChange={(value) => {
                      modifyAppointmentData({
                        allowDuplicateCustomerOnAppointment: value,
                      });
                    }}
                    isCompact
                  />

                  <LimeSwitch
                    title={t`Prikaži termin na spletni formi`}
                    checked={appointmentData.showOnFormPreScheduled}
                    onValueChange={(value) => {
                      modifyAppointmentData({
                        showOnFormPreScheduled: value,
                      });
                    }}
                    isCompact
                  />

                  {appointmentData.repeatingAppointment == true ? (
                    <LimeSwitch
                      title={t`Posodobi vse prihodnje ponavljajoče termine`}
                      description={t`Termini se bodo posodobili na zgoraj podane podatke (razen datuma).`}
                      checked={
                        appointmentData.updateFutureRepeatingAppointments
                      }
                      onValueChange={(value) => {
                        modifyAppointmentData({
                          updateFutureRepeatingAppointments: value,
                        });
                      }}
                      isCompact
                    />
                  ) : null}

                  {(appointmentData.repeatingAppointment ||
                    appointmentData.fixedAppointment) && (
                    <LimeSwitch
                      data-identifier={"fixed-appointment-switch"}
                      title={t`Redni termin`}
                      checked={!!appointmentData.fixedAppointment}
                      onValueChange={(value) => {
                        modifyAppointmentData({
                          fixedAppointment: value,
                        });
                      }}
                      isCompact
                    />
                  )}

                  <Button
                    color="primary"
                    variant="flat"
                    size="sm"
                    isLoading={isPuttingPrescheduledAppointmentData}
                    onPress={async () => {
                      await putPrescheduledAppointmentData({
                        params: {
                          appointmentId,
                        },
                        body: {
                          serviceId: selectedServices[0].serviceId,
                          users: selectedServices[0].users,
                          resources: selectedServices[0].resources,
                          numberOfPeople: appointmentData.numberOfPeople,
                          startTime: appointmentData.startTime,
                          duration:
                            (selectedServices[0].startDuration || 0) +
                            (selectedServices[0].endDuration || 0) +
                            (selectedServices[0].timeOffDuration || 0),
                          timeOffStart: selectedServices[0].startDuration,
                          timeOffDuration: selectedServices[0].timeOffDuration,
                          blockBeforeMins: selectedServices[0].blockBeforeMins,
                          blockAfterMins: selectedServices[0].blockAfterMins,
                          allowDuplicateCustomerOnAppointment:
                            appointmentData.allowDuplicateCustomerOnAppointment,
                          showOnFormPreScheduled:
                            appointmentData.showOnFormPreScheduled,
                          updateFutureRepeatingAppointments:
                            appointmentData.repeatingAppointment == true
                              ? appointmentData.updateFutureRepeatingAppointments
                              : undefined,
                          isFixed: !!appointmentData.fixedAppointment,
                          throwIfRestricted: true,
                        },
                      });

                      addToast({
                        title: t`Podatki skupinskega termina posodobljeni`,
                        color: "success",
                      });

                      fetchAppointmentData(appointmentId);
                    }}
                  >
                    <Trans>Posodobi podatke skupinskega termina</Trans>
                  </Button>
                </div>

                <Divider className="my-2" />
              </>
            ) : null}

            {content}
          </div>
          {!!isPreScheduled && (
            <div className="relative w-full md:w-fit md:min-w-[400px]">
              <CustomerList
                appointmentId={appointmentId}
                customers={appointmentData.customers}
                selectedUserAppointment={selectedUserAppointment}
                handleUserAppointmentSelect={handleUserAppointmentSelect}
                openCustomerCard={openCustomerCard}
                fetchAppointmentData={fetchAppointmentData}
                fixedAppointmentId={appointmentData.fixedAppointmentId ?? null}
                fixedAppointmentMemberCustomerIds={
                  appointmentData.fixedAppointmentMemberCustomerIds ?? []
                }
                serviceId={appointmentData.appointmentParts?.[0]?.serviceId}
              />
            </div>
          )}
        </div>
      )}

      {selectedCustomerAppointment && isFeatureEnabled("serviceAddons") ? (
        <ManageUserAppointmentAddons
          isOpen={isManageUserAppointmentAddonsOpen}
          onClose={(data) => {
            onCloseManageUserAppointmentAddons();

            if (data) {
              fetchAppointmentData(appointmentId);
            }
          }}
          locationId={appointmentData.locationId}
          userAppointments={
            isPreScheduled
              ? [
                  {
                    userAppointmentId:
                      selectedCustomerAppointment.userAppointmentId,
                    serviceId: selectedCustomerAppointment.serviceId,
                    addons: selectedCustomerAppointment.addons,
                  },
                ]
              : appointmentData.customers.map((ca) => ({
                  userAppointmentId: ca.userAppointmentId,
                  serviceId: ca.serviceId,
                  addons: ca.addons,
                }))
          }
        />
      ) : null}
    </div>
  );
};

const DiscountCodeUsageDetails = ({ discountCodeUsages }) => {
  return (
    <LimeAccordion
      itemClasses={{
        base: cn("px-3 rounded-xl bg-secondary-50"),
      }}
    >
      <AccordionItem
        textValue="Discount code usage"
        title={
          <div className="flex items-center gap-1">
            <Ticket size={16} className="text-secondary-800 mr-3" />
            <p className="text-secondary-800 flex items-center text-xs font-medium">
              <Trans>Unovčene kode za popust</Trans>
            </p>
          </div>
        }
      >
        <div className="flex flex-col gap-2">
          {discountCodeUsages.map((usage, index) => (
            <div key={index} className="flex flex-col gap-1 text-xs">
              <div className="flex gap-2">
                <p className="font-medium">
                  <Trans>Koda</Trans>
                </p>
                <p>{usage.code}</p>
              </div>
              <div className="flex gap-2">
                <p className="font-medium">
                  <Trans>Unovčeno</Trans>
                </p>
                <p>{usage.redeemedAt}</p>
              </div>
              {index < discountCodeUsages.length - 1 && (
                <Divider className="my-1" />
              )}
            </div>
          ))}
        </div>
      </AccordionItem>
    </LimeAccordion>
  );
};

const GiftCardTransactionDetails = ({ giftCardTransaction }) => {
  return (
    <LimeAccordion
      itemClasses={{
        base: cn("px-3 rounded-xl bg-success-50"),
      }}
    >
      <AccordionItem
        textValue="Redeemed gift card"
        title={
          <div className="flex items-center gap-1">
            <RiGiftFill size={16} className="text-success-800 mr-3" />
            <p className="text-success-800 flex items-center text-xs font-medium">
              <Trans>Unovčen darilni bon</Trans>
            </p>
          </div>
        }
      >
        <div className="flex flex-col gap-1 text-xs">
          <p>
            <span>
              <Trans>Koda</Trans>
            </span>
            :{" "}
            <Link
              href={`/dashboard/gift-card/${giftCardTransaction.giftCard.id}`}
              showAnchorIcon
              className="ml-1 w-fit text-left"
              size="sm"
              color="secondary"
            >
              {giftCardTransaction.giftCard.code}
            </Link>
          </p>
          <p>
            <span>
              <Trans>Vrednost</Trans>
            </span>
            : <span>{giftCardTransaction.amountFormatted}</span>
          </p>
        </div>
      </AccordionItem>
    </LimeAccordion>
  );
};

const LeftToPayWarning = ({ userAppointments }) => {
  if (userAppointments == null || userAppointments.length === 0) {
    return null;
  }

  const price = userAppointments.reduce((acc, customer) => {
    return acc + customer.price * 100;
  }, 0);

  // pricePaidCents is the full line price (unit price * quantity)
  const addonsAmountCents = userAppointments.reduce(
    (acc, customer) =>
      acc +
      (customer.addons ?? []).reduce(
        (addonsAcc, addon) => addonsAcc + addon.pricePaidCents,
        0,
      ),
    0,
  );

  // Because the same gift card transaction is logged for each linked user appointment
  const giftCardAmount =
    userAppointments[0].giftCardTransaction?.transactionAmountCents ?? 0;

  // Because the same online payment is logged for each linked user appointment
  const onlinePaymentAmount =
    userAppointments[0].onlinePaymentInfo?.amountPaidCents ?? 0;

  if (giftCardAmount === 0 && onlinePaymentAmount === 0) {
    return null;
  }

  const discountAmountCents = (() => {
    if (userAppointments[0].discountType === "percentage") {
      return (price * userAppointments[0].discountAmount) / 100;
    }

    return userAppointments[0].discountAmount;
  })();

  const leftToPayCents =
    price +
    addonsAmountCents -
    giftCardAmount -
    onlinePaymentAmount -
    discountAmountCents;
  const leftToPay = Math.round(leftToPayCents) / 100;

  if (leftToPay <= 0) {
    return null;
  }

  const currency = userAppointments[0].currency ?? "EUR";

  const leftToPayFormatted = formatCurrency({
    amount: leftToPay,
    currency,
    locale: i18n.locale,
  });

  const priceFormatted = formatCurrency({
    amount: price / 100,
    currency,
    locale: i18n.locale,
  });

  const giftCardAmountFormatted =
    giftCardAmount > 0
      ? formatCurrency({
          amount: giftCardAmount / 100,
          currency,
          locale: i18n.locale,
        })
      : null;

  const onlinePaymentAmountFormatted = formatCurrency({
    amount: onlinePaymentAmount / 100,
    currency,
    locale: i18n.locale,
  });

  const addonsAmountFormatted =
    addonsAmountCents > 0
      ? formatCurrency({
          amount: addonsAmountCents / 100,
          currency,
          locale: i18n.locale,
        })
      : null;

  const discountAmountFormatted = formatCurrency({
    amount: discountAmountCents / 100,
    currency,
    locale: i18n.locale,
  });

  return (
    <LimeAccordion
      itemClasses={{
        base: cn("px-3 rounded-xl bg-danger-50"),
      }}
    >
      <AccordionItem
        key={"left-to-pay"}
        textValue="Left to pay"
        title={
          <div className="text-danger-800 flex items-center gap-1 text-xs font-medium">
            <TriangleAlert size={16} className="mr-3" />
            <p>
              <Trans>Stranka še mora doplačati {leftToPayFormatted}</Trans>
            </p>
          </div>
        }
      >
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <p className="text-xs font-medium">
              <Trans>Cena termina</Trans>
            </p>
            <p className="text-xs">{priceFormatted}</p>
          </div>
          {addonsAmountFormatted != null && (
            <div className="flex gap-2">
              <p className="text-xs font-medium">
                <Trans>Dodatki</Trans>
              </p>
              <p className="text-xs">{addonsAmountFormatted}</p>
            </div>
          )}
          {discountAmountCents > 0 && (
            <div className="flex gap-2">
              <p className="text-xs font-medium">
                <Trans>Popust</Trans>
              </p>
              <p className="text-xs">-{discountAmountFormatted}</p>
            </div>
          )}
          {giftCardAmountFormatted != null && (
            <div className="flex gap-2">
              <p className="text-xs font-medium">
                <Trans>Unovčen darilni bon</Trans>
              </p>
              <p className="text-xs">-{giftCardAmountFormatted}</p>
            </div>
          )}
          <div className="flex gap-2">
            <p className="text-xs font-medium">
              <Trans>Spletno plačilo</Trans>
            </p>
            <p className="text-xs">-{onlinePaymentAmountFormatted}</p>
          </div>
        </div>
      </AccordionItem>
    </LimeAccordion>
  );
};

const PaymentDetails = ({ customer }) => {
  const { data: disputeData } = api.stripe.useGetPaymentDispute(
    customer?.userAppointmentId,
    customer?.paymentStatus != null,
  );

  if (customer == null || customer.paymentStatus == null) {
    return null;
  }

  return (
    <>
      <PaymentDisputeCard dispute={disputeData?.dispute} />
      <LimeAccordion
        itemClasses={{
          base: cn("px-3 rounded-xl", {
            "bg-success-50":
              customer.onlinePaymentInfo.paymentStatus === "succeeded",
            "bg-warning-50":
              customer.onlinePaymentInfo.paymentStatus ===
              "requires_payment_method",
          }),
        }}
      >
        <AccordionItem
          key={"payment"}
          textValue="Payment details"
          title={
            <div
              className={cn("flex items-center gap-1 text-xs font-medium", {
                "text-success-800":
                  customer.onlinePaymentInfo.paymentStatus === "succeeded",
                "text-warning-800":
                  customer.onlinePaymentInfo.paymentStatus ===
                  "requires_payment_method",
              })}
            >
              {customer.onlinePaymentInfo.paymentStatus === "succeeded" ? (
                <Check size={16} className="mr-3" />
              ) : (
                <TriangleAlert size={16} className="mr-3" />
              )}
              <p>
                <Trans>Spletno plačilo</Trans>
              </p>
            </div>
          }
        >
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              <p className="text-xs font-medium">
                <Trans>Stanje</Trans>
              </p>
              <p className="text-xs">
                {customer.onlinePaymentInfo.paymentStatus === "succeeded"
                  ? t`Termin potrjen s plačilom preko spleta.`
                  : t`Plačilo termina v obdelavi`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium">
                <Trans>Številka</Trans>
              </p>
              <Link
                href={`/dashboard/stripe/payments?uid=${customer.userAppointmentId}`}
                showAnchorIcon
                color="secondary"
                size="sm"
              >
                {customer.userAppointmentId}
              </Link>
            </div>
            {customer.onlinePaymentInfo.amountPaid != null &&
            customer.onlinePaymentInfo.leftToPay != null ? (
              <>
                <div className="flex gap-2">
                  <p className="text-xs font-medium">
                    <Trans>Že plačano</Trans>
                  </p>
                  <p className="text-xs">
                    {customer.onlinePaymentInfo.amountPaid}
                  </p>
                </div>
                <div className="flex gap-2">
                  <p className="text-xs font-medium">
                    <Trans>Ostanek za plačilo</Trans>
                  </p>
                  <p className="text-xs">
                    {customer.onlinePaymentInfo.leftToPay}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <p className="text-error-800 text-xs font-medium">
                    <Trans>Podrobnosti o plačilu niso na voljo</Trans>
                  </p>
                </div>
              </>
            )}
          </div>
        </AccordionItem>
      </LimeAccordion>
    </>
  );
};

const AddonDetails = ({ addons }) => {
  const services = useCalendarStore((state) => state.services);

  const addonsByService = useMemo(() => {
    return addons.reduce((acc, addon) => {
      const service = services.find((s) => s.serviceId === addon.serviceId);
      if (service) {
        acc[service.name] = [...(acc[service.name] || []), addon];
      }
      return acc;
    }, {});
  }, [addons, services]);

  return (
    <LimeAccordion
      itemClasses={{
        base: cn("px-3 rounded-xl bg-success-50"),
      }}
    >
      <AccordionItem
        textValue={t`Dodatki`}
        title={
          <div className="flex items-center gap-1">
            <HeartPlus size={16} className="text-primary-800 mr-3" />
            <p className="text-primary-800 flex items-center text-xs font-medium">
              <Trans>Dodatki</Trans>
            </p>
          </div>
        }
      >
        {Object.entries(addonsByService).map(([serviceName, addons]) => (
          <div key={serviceName} className="mb-2 flex flex-col gap-1">
            <p className="font-medium">{serviceName}</p>

            {addons.map((addon) => (
              <div key={addon.id} className="flex gap-2">
                <p className="text-xs font-medium">
                  {addon.name} ({addon.quantity})
                </p>
                <p className="text-xs">{addon.priceFormatted}</p>
              </div>
            ))}
          </div>
        ))}
      </AccordionItem>
    </LimeAccordion>
  );
};

export default AppointmentModal;
